import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { DocumentRequestDto } from '@area-restrita/api-client/model/documentRequestDto';
import { getGetDocumentRequestsQueryOptions } from '@area-restrita/api-client/endpoints/document-request/document-request.gen';
import {
  statusMap,
  statusOptions,
} from '@area-restrita/shared/document/document-requests';

import { useQueryTable } from '@/hooks/useQueryTable';
import DataGrid from '@/components/DataGrid';
import PageContainer from '@/components/layout/PageContainer';
import DataTableFacetedFilter from '@/components/DataTable/DataTableFacetedFilter';
import { Button } from '@/components/ui/button';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

const columns: ColumnDef<DocumentRequestDto>[] = [
  { id: 'id', header: 'ID', accessorKey: 'id' },
  {
    id: 'Cliente',
    header: 'Cliente',
    accessorFn: (row) => row.responsible.companyName,
  },
  { id: 'document', header: 'Documento', accessorKey: 'document' },
  {
    id: 'address',
    header: 'Endereço',
    accessorFn: (row) => row.reasonFields.new_address,
  },
  {
    id: 'createdAt',
    header: 'Dt. Solicitação',
    accessorFn: (row) => format(parseISO(row.createdAt), 'dd/MM/yyyy'),
  },
  {
    id: 'implantationDate',
    header: 'Dt. Implantação',
    accessorFn: (row) => format(parseISO(row.implantationDate), 'dd/MM/yyyy'),
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (row) => statusMap[row.status].label,
  },
];

const TenantDocumentRequests: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuthorization } = useAuthSessionStore();
  const canCreate = checkAuthorization(({ can }) =>
    can('create', 'document_requests'),
  );

  const {
    query: { isLoading },
    table,
  } = useQueryTable({
    columns,
    getQueryOptions: getGetDocumentRequestsQueryOptions,
  });

  const canViewDetails = checkAuthorization(({ can }) =>
    can('viewDetails', 'document_requests'),
  );

  return (
    <PageContainer title="Solicitações de Documentos">
      <DataGrid
        variant="card"
        table={table}
        isLoading={isLoading}
        toolbar={
          <div className="flex items-center space-x-2">
            <DataTableFacetedFilter
              title="Status"
              options={statusOptions}
              column={table.getColumn('status')}
            />
          </div>
        }
        actions={canCreate && <Button size="sm">Nova Solicitação</Button>}
        onRowClick={
          canViewDetails &&
          ((row: DocumentRequestDto) => {
            navigate({
              to: '/document-requests/$documentRequestId',
              params: { documentRequestId: row.id },
            });
          })
        }
      />
    </PageContainer>
  );
};

export default TenantDocumentRequests;
